import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { AxiosError } from "axios";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";
import { toast } from "sonner";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { getAllEvaluationPeriodByLevel } from "../../api/generalParam";
import { Select, Table } from "antd";
import { getAllLevel, getClassByLevel } from "../../api/pedagogicElement";
import { ExamPlanningTable } from "../../core/data/interface";
import InfosAlertYearDisabled from "../../core/common/alerts/infosAlertYearDisabled";
import CreateExamModal from "./createExamModal";
import {
  deleteExamPlanning,
  generateClassExamPlanning,
  generateExamEnveloppe,
  getClassExamPlanning,
} from "../../api/planningExam";
import AperçuPlanningExamClass from "./AperçuPlanningExamClass";
import AperçuEnveloppeExam from "./AperçuEnveloppeExam";
import EditExamPlanning from "./editExamPlanning";

// interface Year {
//   id: string;
//   label: string;
//   status: string;
// }

// bouton protégée pour créer une année universitaire
const ProtectedBtnAddExam = withPermission(
  (props: any) => (
    <button
      className="btn btn-primary"
      onClick={() => props.setShowCreate(true)}
    >
      <i className="ti ti-square-rounded-plus me-2" />
      Ajouter un Examen
    </button>
  ),
  [buildPermission(Resource.EDTE, Action.CREATE)],
  "masquer",
);

// boutton protégée pour modifier un examen
const ProtectedBtnUpdateExam = withPermission(
  (props: any) => (
    <li>
      <Link
        className="dropdown-item rounded-1"
        to="#"
        onClick={() => {
          props.setEditExam(props.record.id);
          props.setShowEdit(true);
        }}
      >
        <i className="ti ti-edit-circle me-2" />
        Modifier
      </Link>
    </li>
  ),
  [buildPermission(Resource.EDTE, Action.UPDATE)],
  "masquer",
);

// boutton protégée pour supprimer un examen
const ProtectedBtnDeleteExam = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => props.showConfirmationAlert(props.record)}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-trash-x me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.EDTE, Action.DELETE)],
  "masquer",
);

const PlanningExam = () => {
  //Initialisation des state utilisés
  const { selectedYear } = useAcademicYear();

  const [allLevel, setAllLevel] = useState<any[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<any>();
  const [allClass, setAllClass] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<any>();
  const [allEvaluationPeriod, setAllEvaluationPeriod] = useState<any[]>([]);
  const [selectedEvaluationPeriod, setSelectedEvaluationPeriod] =
    useState<any>(null);
  const [editExam, setEditExam] = useState("");
  const [allExams, setAllExams] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données
  const [isGeneratingPlanning, setIsGeneratingPlanning] = useState(false);
  const [isGeneratingEnveloppe, setIsGeneratingEnveloppe] = useState(false);

  // state pour le PDF
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [showEnveloppeModal, setShowEnveloppeModal] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  //Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);

  const formatExamDay = (date: string) => {
    const shortDay = new Date(date).toLocaleDateString("fr-FR", {
      weekday: "short",
    });

    return (
      shortDay.replace(".", "").charAt(0).toUpperCase() +
      shortDay.replace(".", "").slice(1)
    );
  };

  const formatExamFullDate = (date: string) =>
    new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getCommonClassesLabel = (record: ExamPlanningTable) =>
    (record.common_classes || [])
      .filter((commonClass) => commonClass?.id !== record.Class?.id)
      .map(
        (commonClass) =>
          `${commonClass?.Level?.abbreviation} ${commonClass?.abbreviation} `,
      )
      .filter(Boolean)
      .join(", ");

  //Reload de la liste
  const reloadData = () => setRefreshKey((prev) => prev + 1);

  // Les colonnes de notre tableau
  const columns = [
    {
      title: "Date",
      dataIndex: "date_exam",
      render: (value: string) => (
        <div
          className="d-flex flex-column align-items-center"
          style={{ minWidth: 88 }}
        >
          <span
            className="fw-bold text-dark"
            style={{ fontSize: "1.55rem", lineHeight: 1 }}
          >
            {formatExamDay(value)}
          </span>
          <span className="text-muted" style={{ fontSize: "0.78rem" }}>
            {formatExamFullDate(value)}
          </span>
        </div>
      ),
      sorter: (a: ExamPlanningTable, b: ExamPlanningTable) =>
        new Date(a.date_exam).getTime() - new Date(b.date_exam).getTime(),
    },
    {
      title: "Heure",
      dataIndex: "sessionStart",
      render: (_: string, record: ExamPlanningTable) => (
        <div>
          <span className="fw-semibold">
            {record.sessionStart} - {record.sessionEnd}
          </span>
        </div>
      ),
      sorter: (a: ExamPlanningTable, b: ExamPlanningTable) =>
        a.sessionStart.localeCompare(b.sessionStart),
    },
    {
      title: "Module",
      dataIndex: "Subject",
      render: (_: any, record: ExamPlanningTable) => (
        <div>
          <div className="fw-semibold">{record.Subject?.label || "-"}</div>
          <div className="text-muted" style={{ fontSize: "0.78rem" }}>
            {record.TeacherProfil?.firstname} {record.TeacherProfil?.lastname}
          </div>
        </div>
      ),
      sorter: (a: ExamPlanningTable, b: ExamPlanningTable) =>
        (a.Subject?.label || "").localeCompare(b.Subject?.label || "", "fr"),
    },
    {
      title: "Classes",
      dataIndex: "Class",
      render: (_: any, record: ExamPlanningTable) => {
        const commonClassesLabel = getCommonClassesLabel(record);

        return (
          <div>
            <div className="fw-semibold">{record.Class?.label || "-"}</div>
            <div className="text-muted" style={{ fontSize: "0.78rem" }}>
              {record.Class?.Level?.abbreviation}{" "}
              {record.Classroom?.label ? `• ${record.Classroom.label}` : ""}
            </div>
            {commonClassesLabel && (
              <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                En commun: {commonClassesLabel}
              </div>
            )}
          </div>
        );
      },
      sorter: (a: ExamPlanningTable, b: ExamPlanningTable) =>
        (a.Class?.label || "").localeCompare(b.Class?.label || "", "fr"),
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (_: string, record: ExamPlanningTable) => (
        <div className="d-flex flex-column gap-1 align-items-start">
          <span
            className={`badge d-inline-flex align-items-center ${record.type === "Examen" ? "badge-primary" : "badge-warning"}`}
          >
            <i
              className={`ti ${record.type === "Examen" ? "ti-file-description" : "ti-pencil"} fs-5 me-1`}
            />
            {record.type}
          </span>
          {record.practical && (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-flask fs-5 me-1" />
              Pratique
            </span>
          )}
        </div>
      ),
      sorter: (a: ExamPlanningTable, b: ExamPlanningTable) =>
        a.type.localeCompare(b.type, "fr"),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: ExamPlanningTable) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                {/* modifier un examen */}
                <ProtectedBtnUpdateExam
                  setEditExam={setEditExam}
                  record={record}
                  setShowEdit={setShowEdit}
                />

                {/* supprimer un examen */}

                <ProtectedBtnDeleteExam
                  showConfirmationAlert={showConfirmationAlert}
                  record={record}
                />
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  //Traitement en alert de la demande de suppression d'examen
  const showConfirmationAlert = (exam: any) => {
    MySwal.fire({
      title: "Supprimer un Examen ?",
      text: `Souhaitez-vous vraiment supprimer l'examen ${exam.Subject?.label} sa suppression entrainera ceux des classes concernées ?`,
      showCancelButton: true,
      // confirmButtonColor: "#00ff00",
      confirmButtonText: "Envoyer!",
      // cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          //On lance la requête de suppression de l'examen
          await deleteExamPlanning(exam?.id);
          MySwal.fire({
            title: "Examen supprimé avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });

          reloadData(); //On reload la liste
        } catch (error) {
          const err = error as AxiosError<any>;
          //On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text: err.response?.data?.message || err.response?.data?.error,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  // useEffect pour charger les données de la liste des examens à chaque changement de l'année universitaire, du niveau, de la classe ou de la période d'évaluation sélectionnés
  const fetchExams = async () => {
    // On attend que selectedYear soit défini
    if (
      !selectedYear?.id ||
      !selectedClass?.id ||
      !selectedEvaluationPeriod?.id
    )
      return;

    setHasLoaded(false);
    setIsLoading(true);

    try {
      const response = await getClassExamPlanning(
        selectedClass?.id,
        selectedEvaluationPeriod?.id,
      );
      const exams = response?.data || [];

      // console.log(exams);
      setAllExams(exams);
    } catch (error: any) {
      // Si erreur (Toast Error)
      // console.log(error);
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsLoading(false); //Les données ne sont plus en chargement
      setHasLoaded(true); //Les données ont été chargé avec succès
    }
  };

  // Fontion pour générer le planning selon la périod , année academique , la classe et l'étudiant
  const generatePlanning = async () => {
    if (!selectedClass?.id || !selectedEvaluationPeriod?.id) return;

    try {
      setIsGeneratingPlanning(true);
      const response = await generateClassExamPlanning(
        selectedClass?.id,
        selectedEvaluationPeriod?.id,
      );
      // console.log("Planning generation : ", response);
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowPlanningModal(true);
      // console.log("relevé généré: ", response);
      toast.success("Planning généré avec succès");
    } catch (error: any) {
      // console.log("erreur génération planning: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsGeneratingPlanning(false);
    }
  };

  // Fontion pour générer les enveloppes d'épreuves selon la périod , année academique , la classe et l'étudiant
  const generateEnveloppe = async () => {
    if (!selectedClass?.id || !selectedEvaluationPeriod?.id) return;

    try {
      setIsGeneratingEnveloppe(true);
      const response = await generateExamEnveloppe(
        selectedClass?.id,
        selectedEvaluationPeriod?.id,
      );
      // console.log("Enveloppe generation : ", response);
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowEnveloppeModal(true);
      // console.log("relevé généré: ", response);
      toast.success("Enveloppe générée avec succès");
    } catch (error: any) {
      // console.log("erreur génération enveloppe: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsGeneratingEnveloppe(false);
    }
  };

  const fetchAllClassByLevel = async () => {
    if (!selectedYear?.id || !selectedLevel?.id) return;
    try {
      const response = await getClassByLevel(
        selectedYear?.id,
        selectedLevel?.id,
        buildPermissionContext(Resource.EDTE, Action.READ),
      );
      const classes =
        (response.data || []).slice().sort((a: any, b: any) => {
          const levelCompare = a.Level.label.localeCompare(
            b.Level.label,
            "fr",
            { numeric: true },
          );
          if (levelCompare !== 0) return levelCompare;
          return a.label.localeCompare(b.label, "fr", { numeric: true });
        }) || [];
      // console.log("Classes: ",classes)
      setAllClass(classes);
      if (classes.length > 0) {
        setSelectedClass(classes[0]);
      } else {
        setSelectedClass(undefined);
      }
    } catch (error: any) {
      // console.log("Erreur lors de la récupération des classes:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchPeriods = async () => {
    if (!selectedLevel?.id) {
      setAllEvaluationPeriod([]);
      setSelectedEvaluationPeriod(null);
      return;
    }
    try {
      const response = await getAllEvaluationPeriodByLevel(
        selectedLevel?.id,
        buildPermissionContext(Resource.EDTE, Action.READ),
      );
      const periods = response.data || [];

      setAllEvaluationPeriod(periods);

      // Sélectionne la première période automatiquement s'il y en a
      if ((periods || []).length > 0) {
        setSelectedEvaluationPeriod(periods[0]);
      }
    } catch (error: any) {
      // console.log("erreur de chargement des périodes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      // Option: reset les periodes en cas d'erreur
      setAllEvaluationPeriod([]);
    }
  };

  const fetchLevels = async () => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    try {
      const response = await getAllLevel(
        selectedYear?.id,
        buildPermissionContext(Resource.EDTE, Action.READ),
      );
      const levels = response?.data || [];
      setAllLevel(levels);

      // Sélectionne le premier niveau automatiquement s'il y en a
      if (levels.length > 0) {
        setSelectedLevel(levels[0]);
      } else {
        setSelectedLevel(undefined);
      }
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.message || "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchLevels();
  }, [selectedYear?.id]);

  useEffect(() => {
    fetchAllClassByLevel();
  }, [selectedYear?.id, selectedLevel?.id]);

  useEffect(() => {
    fetchPeriods();
  }, [selectedLevel?.id]);

  useEffect(() => {
    fetchExams();
  }, [selectedClass?.id, selectedEvaluationPeriod?.id, refreshKey]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Planning des Examens</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="#">Étudiants</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Planning Examens
                  </li>
                </ol>
              </nav>
            </div>
            {/* Boutton en entête de la page */}
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {/* <TooltipOption/> */}
            </div>
          </div>
          {/* /Page Header */}

          {!selectedYear?.statut && <InfosAlertYearDisabled />}
          {/* Tableau liste des examens */}
          <div className="card">
            <div className="card-header ">
              <h4 className="mb-3">Liste des Examens</h4>
              <div className="row align-items-center">
                <div className="col-md-2">
                  <Select
                    showSearch
                    size="large"
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="Sélectionner un niveau"
                    value={selectedLevel?.id || undefined}
                    onChange={(levelId) => {
                      const nextLevel = allLevel.find(
                        (l: any) => l.id === levelId,
                      );
                      setSelectedLevel(nextLevel);
                      setSelectedEvaluationPeriod(null);
                    }}
                  >
                    {allLevel.map((level: any) => (
                      <Select.Option
                        key={level.id}
                        value={level.id}
                        label={level.label ?? ""}
                      >
                        {level.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div className="col-md-2">
                  <Select
                    showSearch
                    size="large"
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="Sélectionner une période d'évaluation"
                    value={selectedEvaluationPeriod?.id || undefined}
                    onChange={(periodId) => {
                      const nextPeriod = allEvaluationPeriod.find(
                        (p: any) => p.id === periodId,
                      );
                      setSelectedEvaluationPeriod(nextPeriod);
                    }}
                  >
                    {allEvaluationPeriod.map((period: any) => (
                      <Select.Option
                        key={period.id}
                        value={period.id}
                        label={period.label ?? ""}
                      >
                        {period.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div className="col-md-4">
                  <Select
                    showSearch
                    size="large"
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="Sélectionner une classe"
                    value={selectedClass?.id || undefined}
                    onChange={(classId) => {
                      const nextClass = allClass.find(
                        (c: any) => c.id === classId,
                      );
                      setSelectedClass(nextClass);
                    }}
                  >
                    {allClass.map((cls: any) => (
                      <Select.Option
                        key={cls.id}
                        value={cls.id}
                        label={cls.label ?? ""}
                      >
                        {cls.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div className="col-md-4">
                  <button
                    className="btn btn-light me-2 d-inline-flex align-items-center"
                    disabled={
                      !selectedClass?.id ||
                      !selectedEvaluationPeriod?.id ||
                      isGeneratingPlanning
                    }
                    onClick={async () => {
                      if (!selectedClass?.id || !selectedEvaluationPeriod?.id)
                        return;
                      await generatePlanning();
                    }}
                    title="Générer le planning"
                  >
                    {isGeneratingPlanning ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <i className="ti ti-calendar-event" />
                    )}
                  </button>
                  <button
                    className="btn btn-light me-2 d-inline-flex align-items-center"
                    disabled={
                      !selectedClass?.id ||
                      !selectedEvaluationPeriod?.id ||
                      isGeneratingPlanning
                    }
                    onClick={async () => {
                      if (!selectedClass?.id || !selectedEvaluationPeriod?.id)
                        return;
                      await generateEnveloppe();
                    }}
                    title="Générer les enveloppes d'épreuves"
                  >
                    {isGeneratingEnveloppe ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <i className="ti ti-file-description" />
                    )}
                  </button>
                  <ProtectedBtnAddExam setShowCreate={setShowCreate} />
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {/* Staffs List (On affiche le tableau qui si les données son chargées même vide) */}
              {isLoading && hasLoaded ? (
                <div className="text-center">
                  <div className="spinner-border" role="status"></div>
                  <span className="visually">Chargement des Données...</span>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={allExams}
                  rowKey="id"
                  // Selection={true}
                />
              )}
              {/* /Staffs List */}
            </div>
          </div>
        </div>
        {/* /Page Header */}

        <CreateExamModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSaved={reloadData}
          classSelected={selectedClass}
          periodSelected={selectedEvaluationPeriod}
          levelSelected={selectedLevel}
        />

        <EditExamPlanning
          show={showEdit}
          onClose={() => setShowEdit(false)}
          onSaved={reloadData}
          idExamPlanning={editExam}
          classSelected={selectedClass}
          periodSelected={selectedEvaluationPeriod}
          levelSelected={selectedLevel}
        />

        <AperçuPlanningExamClass
          show={showPlanningModal}
          metaData={{
            pdfBlobUrl,
            classLabel: selectedClass?.label,
            periodLabel: selectedEvaluationPeriod?.label,
            level: selectedLevel?.abbreviation,
            academicYear: selectedYear?.label,
          }}
          onClose={() => setShowPlanningModal(false)}
        />

        <AperçuEnveloppeExam
          show={showEnveloppeModal}
          metaData={{
            pdfBlobUrl,
            classLabel: selectedClass?.label,
            periodLabel: selectedEvaluationPeriod?.label,
            level: selectedLevel?.abbreviation,
            academicYear: selectedYear?.label,
          }}
          onClose={() => setShowEnveloppeModal(false)}
        />
      </div>
    </>
  );
};

// export default withPermission(AcademicYearList, "admin:access", "accès-refuser");
export default PlanningExam;
